import React, {useState, useContext, useEffect, useRef} from "react";
import {azureTokenContext, userInfoContext, userIdContext} from "../App";
import {targetUserIdContext} from "./MainPart";
import Loading from "./Serving/Loading";
import Contracts from "./Serving/Contracts";
import SelectTargetUser from "./Serving/PriorityTableComponents/SelectTargetUser";

import * as ApiCalls from "./Serving/ApiCalls";
import UnresolvedValidationsBox from "./Serving/UnresolvedValidationsBox";

import {Container, TextField, Grid, Alert} from "@mui/material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import {Link} from "react-router-dom";

import "../style/editProfile.css";
import LinkToMainPage from "./Serving/LinkToMainPage";

export default function Profile() {
    const azureToken = useContext(azureTokenContext);
    const [userInfo, setUserInfo] = useContext(userInfoContext);
    const userId = useContext(userIdContext);
    const targetUserContext = useContext(targetUserIdContext);
    const targetUserId = Array.isArray(targetUserContext) ? targetUserContext[0] : null;
    const [loading, setLoading] = useState(true);
    const [targetProfileInfo, setTargetProfileInfo] = useState<any>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const viewModeRef = useRef(false);

    const isViewingOtherUser = Boolean(targetUserId && userId && targetUserId !== userId);
    viewModeRef.current = isViewingOtherUser;
    const profileData = isViewingOtherUser ? targetProfileInfo : userInfo;

    // Self-viewing: fetch sensitive info if missing (existing behavior)
    useEffect(() => {
        if (isViewingOtherUser) return;

        const ckeckData = async () => {
            if (userInfo.Id) {
                if (!hasSensitiveInfo()) {
                    await addSensitiveInfoToUserInfo();
                }
                if (!viewModeRef.current) {
                    setLoading(false);
                }
            }
        };

        ckeckData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo.Id, isViewingOtherUser]);

    // Other user viewing: fetch their full profile
    useEffect(() => {
        if (!isViewingOtherUser) {
            setTargetProfileInfo(null);
            setLoadError(null);
            return;
        }

        let isCurrent = true;

        const fetchTargetProfile = async () => {
            setLoading(true);
            setTargetProfileInfo(null);
            setLoadError(null);
            try {
                const targetUser = await ApiCalls.getTargetUserInfo(targetUserId, azureToken);
                delete targetUser.SocialSecurityNumber;
                delete targetUser.BankAccountNumber;
                if (isCurrent) setTargetProfileInfo(targetUser);
            } catch (e) {
                log("ERROR", "Failed to fetch target user profile", "AuthorsPortal", "", "");
                if (isCurrent) setLoadError("Kunne ikke laste brukerprofilen. Prøv igjen senere.");
            }
            if (isCurrent) setLoading(false);
        };

        fetchTargetProfile();
        return () => { isCurrent = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetUserId, isViewingOtherUser]);

    const addSensitiveInfoToUserInfo = async () => {
        await ApiCalls.GetUserSensitiveInfo(userInfo, azureToken)
            .then((res) => {
                const newUserInfo = {
                    ...userInfo,
                    SocialSecurityNumber: res.SocialSecurityNumber,
                    BankAccountNumber: res.BankAccountNumber,
                };
                setUserInfo(newUserInfo);
            })
            .catch((e) => {
                log("ERROR", "Failed GetUserSensitiveInfo " + e.message, "AuthorsPortal", "", "");
            })
            .finally(() => {
                if (!viewModeRef.current) {
                    setLoading(false);
                }
            });
    };

    const hasSensitiveInfo = () => {
        return userInfo.SocialSecurityNumber || userInfo.BankAccountNumber;
    };

    const styleMUITextFields = {
        backgroundColor: "#f4f4f4c5",
        borderRadius: "8px",

        "& .MuiInputBase-root": {
            pointerEvents: "none", // Disable hover effects
        },
    };

    const styleMUITextFieldsOpaque = {
        ...styleMUITextFields,
        "& .MuiInputBase-input": {
            color: "transparent",
            textShadow: "0 0 8px rgba(0,0,0,0.5)",
            userSelect: "none",
        },
    };

    const styleMUIGrid = {
        padding: "6px",
        backgroundColor: "#e9eeef",
    };

    const log = (level, message, method, workItemId, userRole) => {
        ApiCalls.log(level, message, method, workItemId, userRole, userInfo.Id, azureToken);
    };

    return (
        <>
            {loading ? (
                <Loading/>
            ) : loadError && isViewingOtherUser ? (
                <>
                    <LinkToMainPage/>
                    <Container className="editProfileMainContainer" maxWidth="xl">
                        <SelectTargetUser/>
                        <Alert severity="error" sx={{mt: 2}}>{loadError}</Alert>
                    </Container>
                </>
            ) : (
                <>
                    <LinkToMainPage/>

                    <Container className="editProfileMainContainer" maxWidth="xl">
                        <SelectTargetUser/>
                        <Grid className="editProfileSubContainer" container spacing={1} key={isViewingOtherUser ? `other-${targetUserId}` : 'self'}>
                            <Grid item xs={12} md={12} lg={12}>
                                <div className="profile-header-row">
                                    <div className="editProfileTitle">
                                        <div className="iconBackground">
                                            <PersonOutlineOutlinedIcon fontSize="large"/>
                                        </div>
                                        <div className="editProfileTitleText">Profil</div>
                                    </div>
                                    {!isViewingOtherUser && (
                                        <Link className="goToChangeProfile" to="/editprofile">
                                            Rediger profil
                                        </Link>
                                    )}
                                </div>
                            </Grid>
                            {!isViewingOtherUser && (
                                <Grid item xs={12} md={12} lg={12}>
                                    <UnresolvedValidationsBox/>
                                </Grid>
                            )}
                            <Grid item xs={12} md={4} lg={4}>
                                <Grid container>
                                    <Grid item xs={12} md={11}>
                                        <div className="editProfileColumnHeader">Kontaktinformasjon</div>
                                    </Grid>

                                    <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                        <TextField
                                            fullWidth
                                            label="Navn"
                                            name="Name"
                                            value={profileData?.Name ?? ""}
                                            variant="outlined"
                                            sx={styleMUITextFields}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                        <TextField
                                            fullWidth
                                            label="E-post"
                                            name="Email"
                                            value={profileData?.Email ?? ""}
                                            variant="outlined"
                                            sx={styleMUITextFields}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                        <TextField
                                            fullWidth
                                            label="Mobilnummer"
                                            name="Phone"
                                            value={profileData?.Phone ?? ""}
                                            variant="outlined"
                                            sx={styleMUITextFields}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Grid container>
                                    <Grid item xs={12} md={11}>
                                        <div className="editProfileColumnHeader">Forfatterprofil</div>
                                    </Grid>
                                    <Grid item xs={12} md={11}>
                                        <div className="imageUploadingDiv">
                                            <div className="imagePreviewDiv">
                                                {profileData?.ImageUrl && profileData.ImageUrl.trim() !== '' ? (
                                                    <img 
                                                        className="profileImage" 
                                                        src={profileData.ImageUrl}
                                                        alt="Profile"
                                                        onError={(e) => {
                                                            e.currentTarget.onerror = null;
                                                            e.currentTarget.src = `${process.env.PUBLIC_URL}/no-profile-image.png`;
                                                        }}
                                                    />
                                                ) : (
                                                    <img 
                                                        className="profileImage" 
                                                        src={`${process.env.PUBLIC_URL}/no-profile-image.png`}
                                                        alt="Profile"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                        <TextField
                                            fullWidth
                                            label="Tittel"
                                            name="Title"
                                            value={profileData?.Title ?? ""}
                                            variant="outlined"
                                            sx={styleMUITextFields}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                        <TextField
                                            fullWidth
                                            label="Arbeidssted"
                                            name="Employer"
                                            value={profileData?.Employer ?? ""}
                                            variant="outlined"
                                            sx={styleMUITextFields}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                        <TextField
                                            fullWidth
                                            label="Forfatterpresentasjon"
                                            name="Description"
                                            value={profileData?.Description ?? ""}
                                            variant="outlined"
                                            sx={styleMUITextFields}
                                            multiline
                                            rows={5}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Grid container>
                                    <Grid item xs={12} md={11}>
                                        <div className="editProfileColumnHeader">Honorar</div>
                                    </Grid>

                                    <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                        <TextField
                                            fullWidth
                                            label="Postadresse (privat)"
                                            name="Address"
                                            value={profileData?.Address ?? ""}
                                            variant="outlined"
                                            sx={styleMUITextFields}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                        <TextField
                                            fullWidth
                                            label="Postnummer"
                                            name="ZipCode"
                                            value={profileData?.ZipCode ?? ""}
                                            variant="outlined"
                                            sx={styleMUITextFields}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                        <TextField
                                            fullWidth
                                            label="Sted"
                                            name="City"
                                            value={profileData?.City ?? ""}
                                            variant="outlined"
                                            sx={styleMUITextFields}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                        <TextField
                                            fullWidth
                                            label="Land"
                                            name="Country"
                                            value={profileData?.Country ?? ""}
                                            variant="outlined"
                                            sx={styleMUITextFields}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                        <TextField
                                            fullWidth
                                            label="Fødselsnummer"
                                            name="SocialSecurityNumber"
                                            value={isViewingOtherUser ? "••••••••••••" : (profileData?.SocialSecurityNumber ?? "")}
                                            variant="outlined"
                                            sx={isViewingOtherUser ? styleMUITextFieldsOpaque : styleMUITextFields}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={11} sx={styleMUIGrid}>
                                        <TextField
                                            fullWidth
                                            label="Kontonummer"
                                            name="BankAccountNumber"
                                            value={isViewingOtherUser ? "••••••••••••" : (profileData?.BankAccountNumber ?? "")}
                                            variant="outlined"
                                            sx={isViewingOtherUser ? styleMUITextFieldsOpaque : styleMUITextFields}
                                        />
                                    </Grid>
                                    <Contracts userId={isViewingOtherUser ? targetUserId : undefined}/>
                                </Grid>
                            </Grid>

                            {/* Empty grid to make it center aligned */}
                            <Grid item xs={4}></Grid>
                        </Grid>
                    </Container>
                </>
            )}
        </>
    );
}
